import { Component, computed, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ConfigurationItem } from "../../_objects/tenant-config";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { LangUpdateService } from "../../_services/lang-update.service";
import { SpinnerService } from "../../_services/spinner.service";
import { TenantConfigService } from "../../_services/http-api/tenant-config.service";
import { CommonSaveConfirmDialogComponent } from "../../common-dialog/common-save-confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { FormBuilder, Validators } from "@angular/forms";
import { FEATURE_FLAGS, TenantConfig } from "../../app.constants";
import { NetworkStatusService } from "../../_services/network-status.service";
import { TenantLogoComponent } from "../tenant-logo/tenant-logo.component";
import { LogoImageOfflineService } from "../../_services/offline/logo-image-offline.service";
import { FeatureFlagService } from "../../_services/feature-flag/feature-flag.service";

@Component({
  selector: "app-settings-tenant",
  templateUrl: "./settings-tenant.component.html",
  styleUrls: ["./settings-tenant.component.scss"],
  standalone: false
})
export class SettingsTenantComponent implements OnInit, OnDestroy {
  existConfig = false;
  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  ownerLabel = TenantConfig.DefaultConfigurationValues.LabelOfOwner;
  countryLabel = TenantConfig.DefaultConfigurationValues.LabelOfCountry;
  stateLabel = TenantConfig.DefaultConfigurationValues.LabelOfState;
  regionLabel = TenantConfig.DefaultConfigurationValues.LabelOfRegion;
  siteLabel = TenantConfig.DefaultConfigurationValues.LabelOfSite;
  projectLabel = TenantConfig.DefaultConfigurationValues.LabelOfProject;
  serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;
  configs: ConfigurationItem[] = [];

  private onDestroy$ = new Subject();

  readonly enableAdvancedFeaturesHeader = "settingTenant.groupheader_enable advanced feature";
  readonly switchApplicationBehaviorHeader = "settingTenant.groupheader_switch application behavior";
  readonly additionalAttributeHeader = "settingTenant.groupheader_additional attributes";
  readonly defineTerminologyHeader = "settingTenant.groupheader_define terminology";

  readonly requiredError = "settingTenant.requiredError";

  readonly defineTerminologyMaxLength = 20;
  readonly defineTerminologyValidators = [Validators.required, Validators.minLength(1), Validators.maxLength(this.defineTerminologyMaxLength)];

  configForm = this.formBuilder.group({
    EnableAdvancedFeatures: this.formBuilder.group({
      IsEnabledAnalytics: [false]
    }),
    SwitchApplicationBehavior: this.formBuilder.group({
      ImageSelectModeWhenCreatingReport: [0],
      IsSelectedAllFolderWhenCreatingReport: [false],
      DefaultMarkWhenUploadingNoMarkImage: [0],
      DefaultUnitOfMeasurementResult: [0],
      DefaultImageQualityOfFinalReports: [0],
      DefaultImageQualityOfDraftReports: [0],
      ShowTagsOnImages: [false]
    }),
    AdditionalAttribute: this.formBuilder.group({
      SubjectAttributeOwner: [0],
      SubjectAttributeCountry: [0],
      SubjectAttributeState: [0],
      SubjectAttributeDistrict: [0],
      SubjectAttributeSite: [0],
      SubjectAttributeProject: [0]
    }),
    DefineTerminology: this.formBuilder.group({
      LabelOfMark: ["", this.defineTerminologyValidators],
      LabelOfTitle: ["", this.defineTerminologyValidators],
      LabelOfOwner: ["", this.defineTerminologyValidators],
      LabelOfCountry: ["", this.defineTerminologyValidators],
      LabelOfState: ["", this.defineTerminologyValidators],
      LabelOfRegion: ["", this.defineTerminologyValidators],
      LabelOfSite: ["", this.defineTerminologyValidators],
      LabelOfProject: ["", this.defineTerminologyValidators],
      LabelOfSerialNumber: ["", this.defineTerminologyValidators],
      LabelOfTags: ["", this.defineTerminologyValidators]
    })
  });

  readonly enableAdvancedFeature = {
    controlName: "IsEnabledAnalytics",
    displayKey: "settingTenant.configuration_IsEnabledAnalytics"
  };

  readonly switchApplicationBehaviorGroup = [
    {
      controlName: "ImageSelectModeWhenCreatingReport",
      displayKey: "settingTenant.configuration_ImageSelectModeWhenCreatingReport",
      selectItems: [
        {
          displayLabel: "settingTenant.value_no",
          value: 0
        },
        {
          displayLabel: "settingTenant.value_yes",
          value: 1
        },
        {
          displayLabel: "settingTenant.value_optional",
          value: 2
        }
      ]
    },
    {
      controlName: "IsSelectedAllFolderWhenCreatingReport",
      displayKey: "settingTenant.configuration_IsSelectedAllFolderWhenCreatingReport",
      selectItems: [
        {
          displayLabel: "settingTenant.value_no",
          value: false
        },
        {
          displayLabel: "settingTenant.value_yes",
          value: true
        }
      ]
    },
    {
      controlName: "DefaultMarkWhenUploadingNoMarkImage",
      displayKey: "settingTenant.configuration_DefaultMarkWhenUploadingNoMarkImage",
      selectItems: [
        {
          displayLabel: "settingTenant.value_a",
          value: 1
        },
        {
          displayLabel: "settingTenant.value_b",
          value: 2
        },
        {
          displayLabel: "settingTenant.value_c",
          value: 3
        },
        {
          displayLabel: "settingTenant.value_d",
          value: 4
        },
        {
          displayLabel: "settingTenant.value_e",
          value: 5
        },
        {
          displayLabel: "settingTenant.value_f",
          value: 6
        },
        {
          displayLabel: "settingTenant.value_x",
          value: 0
        }
      ]
    },
    {
      controlName: "DefaultUnitOfMeasurementResult",
      displayKey: "settingTenant.configuration_DefaultUnitOfMeasurementResult",
      selectItems: [
        {
          displayLabel: "settingTenant.value_mm",
          value: 0
        },
        {
          displayLabel: "settingTenant.value_inch",
          value: 1
        }
      ]
    },
    {
      controlName: "DefaultImageQualityOfFinalReports",
      displayKey: "settingTenant.configuration_DefaultImageQualityOfFinalReports",
      selectItems: [
        {
          displayLabel: "settingTenant.value_high",
          value: 0
        },
        {
          displayLabel: "settingTenant.value_medium",
          value: 1
        },
        {
          displayLabel: "settingTenant.value_low",
          value: 2
        }
      ]
    },
    {
      controlName: "DefaultImageQualityOfDraftReports",
      displayKey: "settingTenant.configuration_DefaultImageQualityOfDraftReports",
      selectItems: [
        {
          displayLabel: "settingTenant.value_high",
          value: 0
        },
        {
          displayLabel: "settingTenant.value_medium",
          value: 1
        },
        {
          displayLabel: "settingTenant.value_low",
          value: 2
        }
      ]
    }
  ];

  readonly radioButtonOptions = [
    {
      displayLabel: "settingTenant.value_hide",
      value: 0
    },
    {
      displayLabel: "settingTenant.value_required",
      value: 1
    },
    {
      displayLabel: "settingTenant.value_optional",
      value: 2
    }
  ];
  readonly additionalAttributeGroup = [
    {
      controlName: "SubjectAttributeOwner",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfOwner"
    },
    {
      controlName: "SubjectAttributeCountry",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfCountry"
    },
    {
      controlName: "SubjectAttributeState",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfState"
    },
    {
      controlName: "SubjectAttributeDistrict",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfRegion"
    },
    {
      controlName: "SubjectAttributeSite",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfSite"
    },
    {
      controlName: "SubjectAttributeProject",
      selectItems: this.radioButtonOptions,
      firstPartDisplayKey: "settingTenant.configuration_LabelOfSerialNumber",
      secondPartDisplayKey: "settingTenant.configuration_LabelOfProject"
    }
  ];

  readonly defineTerminology = [
    {
      controlName: "LabelOfMark",
      displayKey: "settingTenant.configuration_LabelOfMark"
    },
    {
      controlName: "LabelOfTitle",
      displayKey: "settingTenant.configuration_LabelOfTitle"
    },
    {
      controlName: "LabelOfOwner",
      displayKey: "settingTenant.configuration_LabelOfOwner"
    },
    {
      controlName: "LabelOfCountry",
      displayKey: "settingTenant.configuration_LabelOfCountry"
    },
    {
      controlName: "LabelOfState",
      displayKey: "settingTenant.configuration_LabelOfState"
    },
    {
      controlName: "LabelOfRegion",
      displayKey: "settingTenant.configuration_LabelOfRegion"
    },
    {
      controlName: "LabelOfSite",
      displayKey: "settingTenant.configuration_LabelOfSite"
    },
    {
      controlName: "LabelOfProject",
      displayKey: "settingTenant.configuration_LabelOfProject"
    },
    {
      controlName: "LabelOfSerialNumber",
      displayKey: "settingTenant.configuration_LabelOfSerialNumber"
    }
  ];

  public logoUrl: string | null = null;

  constructor(
    private tenantConfigService: TenantConfigService,
    private snackbar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private logoImageService: LogoImageOfflineService,
    private networkStatusService: NetworkStatusService,
    private featureFlagService: FeatureFlagService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit(): void {
    this.getTenantConfigs();
    this.addTagsRelatedItemsDynamically();
    this.getLogo();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  private addTagsRelatedItemsDynamically() {
    this.networkStatusService.isOnPremise$.pipe(takeUntil(this.onDestroy$)).subscribe((isStandalone) => {
      if (!isStandalone || this.networkStatusService.isBrianMode) {
        this.addShowTagsOnImagesOption();
        this.addTagsLabelInput();
      }
    });
  }

  private addShowTagsOnImagesOption() {
    this.switchApplicationBehaviorGroup.push({
      controlName: "ShowTagsOnImages",
      displayKey: "settingTenant.configuration_ShowTagsOnImages",
      selectItems: [
        {
          displayLabel: "settingTenant.value_no",
          value: false
        },
        {
          displayLabel: "settingTenant.value_yes",
          value: true
        }
      ]
    });
  }

  private addTagsLabelInput() {
    this.defineTerminology.push({
      controlName: "LabelOfTags",
      displayKey: "settingTenant.configuration_LabelOfTags"
    });
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.configForm.dirty) {
      return true;
    }

    const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
      width: "432px",
      disableClose: true,
      data: {
        isMove: true,
        isError: this.configForm.invalid,
        message: "settingTenant.message",
        customMessage: "settingTenant.customMessage",
        notSaveBtnLabel: "settingTenant.notSaveBtnLabel"
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result === false) {
      dialogRef.close();
      return true;
    } else if (result === "") {
      dialogRef.close();
      return false;
    } else {
      this.submit();
      return true;
    }
  }

  public setConfigs() {
    this.existConfig = false;

    this.configForm.reset();
    this.configs.forEach((c) => {
      if (this.configForm.controls[c.groupKey] && this.configForm.controls[c.groupKey].controls[c.key]) {
        this.configForm.controls[c.groupKey].controls[c.key].patchValue(c.value as any);
      }
    });

    this.tenantConfigService.setTenantConfig(this.configs);
    this.existConfig = true;
  }

  private getTenantConfigs(): void {
    this.oscSpinnerService.show(true);
    this.tenantConfigService
      .getTenantConfigs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (res) => {
          this.configs = res;
          this.setConfigs();
        },
        (error) => {
          console.log(error);
          this.oscSpinnerService.hide(true);
        },
        () => this.oscSpinnerService.hide(true)
      );
  }

  getUpdateData() {
    const list = [];
    Object.keys(this.configForm.controls).forEach((groupKey) => {
      Object.keys(this.configForm.controls[groupKey].controls).forEach((key) => {
        list.push({
          key: key,
          value: this.configForm.controls[groupKey].controls[key].value
        });
      });
    });
    return list;
  }

  public get isGeConnectorFeatureFlagEnabled() {
    return this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.GE_CONNECTOR);
  }

  public isAssetModuleEnabled = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE);

  private readonly assetModuleTerminologyControls = ["LabelOfMark", "LabelOfTitle", "LabelOfTags"];

  public filteredDefineTerminology = computed(() => {
    if (this.isAssetModuleEnabled()) {
      return this.defineTerminology.filter((t) => this.assetModuleTerminologyControls.includes(t.controlName));
    }
    return this.defineTerminology;
  });

  /**
   * 保存処理
   */
  public submit() {
    if (!this.configForm.dirty || this.configForm.invalid) {
      return;
    }

    this.oscSpinnerService.show();
    this.tenantConfigService.putTenantConfigs(this.getUpdateData()).subscribe(
      (res) => {
        this.configs = res;
        this.setConfigs();
      },
      (error) => {
        console.log(error);
        this.snackbar.open(this.translate.instant("settingTenant.putTenantConfigsError"));
        this.oscSpinnerService.hide();
      },
      () => {
        this.snackbar.open(this.translate.instant("settingTenant.putTenantConfigsSuccessfully"));
        this.oscSpinnerService.hide();
      }
    );
  }

  public openImportLogoManagement() {
    const dialogRef = this.dialog.open(TenantLogoComponent, { width: "800px", height: "450px" });

    dialogRef.afterClosed().subscribe(() => {
      dialogRef.close();
      this.getLogo();
    });
  }

  private getLogo() {
    this.logoImageService.getLogoImage().subscribe((logo) => {
      this.logoUrl = logo;
    });
  }

  public isBrianApp() {
    return this.networkStatusService.isBrianMode;
  }

  public isAppOnline() {
    return this.networkStatusService.isOnline;
  }
}
